import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../config";

type Ctx = CanvasRenderingContext2D & { roundRect?: (x: number, y: number, w: number, h: number, r: number) => void };

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const c = ctx as Ctx;
  if (typeof c.roundRect === "function") {
    ctx.beginPath();
    c.roundRect(x, y, w, h, r);
    return;
  }
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

export function fillRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  roundedRectPath(ctx, x, y, w, h, r);
  ctx.fill();
}

export function strokeRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  roundedRectPath(ctx, x, y, w, h, r);
  ctx.stroke();
}

// Shared "menu" colours for the dark utility scenes.
export const UI = {
  bgTop: "#252e42",
  bgBottom: "#11151f",
  accent: "#f0d98a",
  parchment: "#f3e7c4",
  parchmentBorder: "#7a5e2a",
  subtitle: "#9fb0c8",
  cardShadow: "rgba(0,0,0,0.35)",
};

// Soft vertical-gradient backdrop for the non-gameplay scenes — replaces the flat
// fill so every utility screen shares a little depth.
export function drawSceneBackground(ctx: CanvasRenderingContext2D): void {
  const g = ctx.createLinearGradient(0, 0, 0, VIRTUAL_HEIGHT);
  g.addColorStop(0, UI.bgTop);
  g.addColorStop(1, UI.bgBottom);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, VIRTUAL_WIDTH, VIRTUAL_HEIGHT);
}

// A parchment card with a soft drop shadow + warm border — the standard surface
// for list rows and panels across scenes.
export function drawCard(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r = 10,
  opts: { fill?: string; border?: string; borderWidth?: number; shadow?: boolean } = {},
): void {
  if (opts.shadow !== false) {
    ctx.save();
    ctx.shadowColor = UI.cardShadow;
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 3;
    ctx.fillStyle = opts.fill ?? UI.parchment;
    fillRoundedRect(ctx, x, y, w, h, r);
    ctx.restore();
  } else {
    ctx.fillStyle = opts.fill ?? UI.parchment;
    fillRoundedRect(ctx, x, y, w, h, r);
  }
  ctx.strokeStyle = opts.border ?? UI.parchmentBorder;
  ctx.lineWidth = opts.borderWidth ?? 2;
  strokeRoundedRect(ctx, x + 0.5, y + 0.5, w - 1, h - 1, r);
}

// Standard back-arrow glyph for the top-left icon button. Previously copy-pasted
// identically into ~10 scenes; now one definition.
export function drawBackIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx + 5, cy - 6);
  ctx.lineTo(cx - 5, cy);
  ctx.lineTo(cx + 5, cy + 6);
  ctx.stroke();
}

// Centered scene header with a short accent underline, for a consistent title.
export function drawSceneTitle(ctx: CanvasRenderingContext2D, text: string, y = 40): void {
  ctx.fillStyle = "#f5e9c8";
  ctx.font = "bold 22px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(text, VIRTUAL_WIDTH / 2, y);
  ctx.fillStyle = UI.accent;
  fillRoundedRect(ctx, VIRTUAL_WIDTH / 2 - 24, y + 7, 48, 3, 1.5);
}

// Small filled "pill" action button (colored fill, white label). The shared shape
// behind Friends/Clan/Quests/Battles inline action buttons.
export function drawPillButton(
  ctx: CanvasRenderingContext2D,
  label: string,
  color: string,
  r: Rect,
): void {
  ctx.fillStyle = color;
  fillRoundedRect(ctx, r.x, r.y, r.w, r.h, 6);
  ctx.fillStyle = "#fff";
  ctx.font = "bold 12px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2);
  ctx.textBaseline = "alphabetic";
}

// Clip a string to `maxW` pixels with an ellipsis. The caller must set the font
// first (measureText uses the current font).
export function truncateToWidth(ctx: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (ctx.measureText(text).width <= maxW) return text;
  let s = text;
  while (s.length > 1 && ctx.measureText(`${s}…`).width > maxW) s = s.slice(0, -1);
  return `${s}…`;
}
